import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "familytable_install_dismissed_at";
const DISMISS_DAYS = 30;

function recentlyDismissed() {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    const ms = Date.now() - new Date(ts).getTime();
    return ms < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, new Date().toISOString());
    } catch { /* ignore */ }
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch { /* ignore */ }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible || !deferred) return null;

  return (
    <div
      className="fixed z-[60] bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm rounded-2xl p-4 flex items-start gap-3 shadow-lg"
      style={{
        background: "var(--hh-cream)",
        border: "1px solid var(--hh-line)",
      }}
      role="dialog"
      aria-label="Install familytable"
      data-testid="install-prompt"
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: "var(--hh-peach)", color: "var(--hh-ink)" }}
      >
        <Download strokeWidth={2.4} className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="text-base font-extrabold leading-tight"
          style={{ fontFamily: "var(--hh-display)" }}
        >
          Install familytable
        </p>
        <p className="text-xs mt-0.5" style={{ color: "var(--hh-ink-soft)" }}>
          Add to your home screen for one-tap access and offline reading.
        </p>
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={install}
            className="nb-btn px-3 py-1.5 text-xs font-bold"
            style={{ background: "var(--hh-sage)" }}
            data-testid="install-prompt-install-btn"
          >
            Install
          </button>
          <button
            onClick={dismiss}
            className="text-xs font-semibold underline-offset-2 hover:underline"
            style={{ color: "var(--hh-ink-soft)" }}
            data-testid="install-prompt-dismiss-btn"
          >
            Not now
          </button>
        </div>
      </div>
      <button
        onClick={dismiss}
        className="shrink-0 rounded-full p-1 hover:bg-black/5"
        aria-label="Dismiss"
        data-testid="install-prompt-close-btn"
      >
        <X strokeWidth={2.4} className="w-4 h-4" />
      </button>
    </div>
  );
}
